import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { FIRM } from "@/lib/content";
import { MessageCircle, Mail, Phone, ExternalLink, Building2, Landmark, ArrowRight } from "lucide-react";

const STEG = [
    {
        n: "01",
        title: "Kontakta ansvarig jurist",
        body: `Vänd dig i första hand till den jurist som handlägger ditt ärende. Beskriv vad du är missnöjd med och vad du vill uppnå. Merparten av alla klagomål löses i detta skede.`,
    },
    {
        n: "02",
        title: "Kontakta byråns ledning",
        body: `Om du inte får önskad återkoppling, skicka en skriftlig beskrivning till byråns ledning via e-post eller brev. Vi bekräftar mottagandet inom 5 arbetsdagar och lämnar ett motiverat besked inom 30 dagar.`,
    },
    {
        n: "03",
        title: "Allmänna reklamationsnämnden (ARN)",
        body: `Konsumenter kan få tvisten prövad gratis av ARN. Anmälan ska göras inom ett år från det att du framställt klagomålet till byrån.`,
        link: { href: "https://www.arn.se", label: "arn.se" },
    },
    {
        n: "04",
        title: "EU:s onlineplattform (ODR)",
        body: `Du kan även anmäla en tvist via EU-kommissionens plattform för tvistlösning online, särskilt praktiskt vid gränsöverskridande ärenden.`,
        link: { href: "https://ec.europa.eu/consumers/odr", label: "ec.europa.eu/consumers/odr" },
    },
    {
        n: "05",
        title: "Allmän domstol",
        body: `I sista hand kan tvist prövas av svensk allmän domstol. För ekonomiska anspråk kan du också vända dig till Kronofogden för betalningsföreläggande.`,
    },
];

export default function Klagomal() {
    useSEO({
        title: "Klagomål",
        description:
            "Så lämnar du ett klagomål till BM Juridik — från första kontakt till Allmänna reklamationsnämnden (ARN) och EU:s ODR-plattform.",
    });

    return (
        <div data-testid="page-klagomal">
            <PageHeader
                kicker="Klagomål"
                title="Vi tar varje synpunkt"
                accent="på allvar."
                description="Om du är missnöjd med vår service eller rådgivning vill vi gärna få veta det. Nedan hittar du vägen från ett första klagomål till de externa instanser som kan pröva tvister om vi inte kan komma överens."
            />

            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {STEG.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.05 }}
                                data-testid={`klagomal-step-${s.n}`}
                                className="bg-[#111113] border border-white/5 p-8 rounded-sm"
                            >
                                <p className="font-serif text-4xl text-[#C4A977]/40 leading-none">
                                    {s.n}
                                </p>
                                <h3 className="mt-5 font-serif text-xl text-[#F4F4F5]">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                                {s.link && (
                                    <a
                                        href={s.link.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`klagomal-link-${s.n}`}
                                        className="mt-5 inline-flex items-center gap-2 text-xs text-[#C4A977] hover:text-[#DBC08F]"
                                    >
                                        {s.link.label}
                                        <ExternalLink size={10} />
                                    </a>
                                )}
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-16 bg-[#111113] border border-[#C4A977]/20 p-8 rounded-sm">
                        <div className="flex items-start gap-4">
                            <span className="w-11 h-11 border border-[#C4A977]/40 grid place-items-center text-[#C4A977] shrink-0">
                                <MessageCircle size={18} />
                            </span>
                            <div>
                                <h3 className="font-serif text-2xl">
                                    Så kontaktar du byråns ledning
                                </h3>
                                <div className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#A1A1AA]">
                                    <a
                                        href={`mailto:${FIRM.email}`}
                                        className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                                        data-testid="klagomal-email"
                                    >
                                        <Mail size={14} /> {FIRM.email}
                                    </a>
                                    <a
                                        href={`tel:${FIRM.phoneTel}`}
                                        className="inline-flex items-center gap-2 hover:text-[#C4A977]"
                                        data-testid="klagomal-phone"
                                    >
                                        <Phone size={14} /> {FIRM.phone}
                                    </a>
                                </div>
                                <p className="mt-5 text-xs text-[#A1A1AA]/80 leading-relaxed">
                                    När du skickar ett klagomål — ange gärna
                                    ärendenummer, handläggarens namn och en
                                    kort beskrivning av vad som hänt.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <a
                            href="https://www.arn.se"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-5 rounded-sm transition-colors"
                        >
                            <span className="flex items-center gap-3">
                                <Building2 size={16} className="text-[#C4A977]" />
                                <span className="text-sm">Allmänna reklamationsnämnden</span>
                            </span>
                            <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#C4A977]" />
                        </a>
                        <a
                            href="https://ec.europa.eu/consumers/odr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 p-5 rounded-sm transition-colors"
                        >
                            <span className="flex items-center gap-3">
                                <Landmark size={16} className="text-[#C4A977]" />
                                <span className="text-sm">EU:s ODR-plattform</span>
                            </span>
                            <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#C4A977]" />
                        </a>
                    </div>

                    <p className="mt-10 text-xs text-[#A1A1AA]/80">
                        Läs mer om dina rättigheter i våra{" "}
                        <Link to="/allmanna-villkor" className="text-[#C4A977] hover:underline">
                            allmänna villkor
                        </Link>{" "}
                        och{" "}
                        <Link to="/integritetspolicy" className="text-[#C4A977] hover:underline">
                            integritetspolicy
                        </Link>
                        .
                    </p>
                </div>
            </section>
        </div>
    );
}
